import { Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
	EmailField,
	FeedbackDescriptionField,
	FeedbackTitleField,
	ImageTag,
	InlineCaptchaField,
	PhoneNumberField,
	SubmitBtn,
} from 'components';
import { enqueueSnackbar } from 'notistack';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAPI, useGPSString, useProduct, useSDK, useUserInfo } from 'stores';
import { MotionWrapper } from 'utils';

const Feedback = () => {
	// 	Get isFirst from the state set in <FeedbackTagBox /> (Set at navigate()).
	const { state } = useLocation();
	const isFirst = state?.isFirst;

	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const product = useProduct();
	const user = useUserInfo();
	const gps = useGPSString();
	const { saveFeedback } = useAPI();
	const { sysInfo } = useSDK();
	const { handleSubmit, control, watch, getValues, setValue } = useForm();

	const onSubmit = (data: any) => {
		saveFeedback({
			uaid: product!.currentReqUAID,
			email: data.mail,
			title: data.feedbackTitle,
			content: data.feedbackDescription,
			captcha: data.captcha,
			udid: '', // No such thing for web
			brand: '', // No such thing for web
			model: sysInfo?.equipmentModel,
			tel: data.phoneNum,
			gps: gps,
			imgs: data.imgs || [],
		})
			.then(() => {
				enqueueSnackbar(t('feedback.success'), {
					variant: 'success',
				}) && (isFirst ? navigate(-1) : navigate(-2));
			})
			.catch((e) => {
				if (e instanceof Error)
					enqueueSnackbar(e.message, { variant: 'error' });
				queryClient.refetchQueries({
					queryKey: ['getValidationCode'],
					exact: true,
				});
			});
	};

	useEffect(() => {
		watch('imgs');
	}, []);

	if (!product) return <></>;

	return (
		<MotionWrapper>
			<Stack
				sx={{
					overflow: 'scroll',
					p: 3,
					pt: 1,
				}}
				spacing={3}
				component="form"
				noValidate
				autoComplete="off"
				onSubmit={handleSubmit(onSubmit)}
			>
				<Stack spacing={1}>
					<Typography>{t('feedback.contact')}</Typography>
					<EmailField control={control} defaultValue={user?.mail} />
					<PhoneNumberField
						control={control}
						defaultValue={user?.phoneNum}
					/>
				</Stack>
				<Stack spacing={1}>
					<Typography>{t('feedback.title')}</Typography>
					<FeedbackTitleField control={control} />
				</Stack>
				<Stack spacing={1}>
					<Typography>{t('feedback.description')}</Typography>
					<FeedbackDescriptionField control={control} />
				</Stack>
				<Stack spacing={1}>
					<Typography>{t('feedback.images')}</Typography>
					<Stack spacing={1} direction="row">
						{getValues('imgs') &&
							getValues('imgs').map(
								(img: string, index: number) => (
									<ImageTag
										key={index}
										img={img}
										control={control}
										getValues={getValues}
										setValue={setValue}
									/>
								)
							)}
						{getValues('imgs') &&
						getValues('imgs').length >= 3 ? null : (
							<ImageTag
								control={control}
								getValues={getValues}
								setValue={setValue}
							/>
						)}
					</Stack>
				</Stack>
				<InlineCaptchaField control={control} />
				<SubmitBtn />
			</Stack>
		</MotionWrapper>
	);
};

export default Feedback;
